import { COMPANY_API, COMPANY_API_NO_RESULT_RESPONSE } from 'apis';
import { normalizeEmployeeCardPaymentCode } from 'constants/employeeCardPayment';
import storage from 'helpers/storage';

/**
 * 사원증 결제 관리 API
 *
 * Authorization / x-comid / x-userid 헤더는 apis/index.tsx 의 request 인터셉터가 넣는다.
 */
const LIST_ENDPOINT = '/company/v2/barCode/member';
const HISTORY_ENDPOINT = '/company/v2/barCode/history';

export interface IEmployeeCardPaymentSearchParams {
  /** ID, 이름, 이메일, 휴대전화번호, 사원번호, 사원증 결제정보 통합 검색어 */
  keyword?: string;
  /** 사용자 상태 (전체면 보내지 않는다) */
  status?: 'ACTIVE' | 'INACTIVE' | null;
  /** 식대그룹 idx */
  groupIdx?: number;
  /** 부서 코드 */
  orgCode?: string;
  /** 사원증 결제정보 등록 여부 (true: 등록, false: 미등록, 전체면 보내지 않는다) */
  hasBarCode?: boolean | null;
  page?: number;
  size?: number;
}

/**
 * BarCodeStatic 은 사용자당 1행 이하라 세 가지 상태를 가진다.
 * - null          : 한 번도 등록한 적 없음
 * - isActive false: 삭제됨(soft delete). barCode 는 마지막 값이라 화면에는 노출하지 않는다
 * - isActive true : 등록 상태
 */
export interface IEmployeeCardPaymentBarCodeInfo {
  barCodeStaticIdx: number;
  barCode?: string;
  isActive?: boolean;
  regDate?: string;
}

/** 응답의 userInfo + barCodeInfo 를 화면에서 쓰기 쉬운 한 겹으로 편다. */
export interface IEmployeeCardPaymentRow {
  userId: string;
  signId: string;
  name: string;
  groupIdx?: number;
  groupName?: string;
  orgCode?: string;
  /** 부서 단일 컬럼에 쓰는 '상위>하위' 전체 경로 */
  orgPathName?: string;
  email?: string;
  /** 응답의 cellphone */
  phone?: string;
  employeeId?: string;
  statusCode?: string;
  statusText?: string;
  joinDate?: string;
  /** 등록 상태(isActive)일 때만 채운다 — 삭제된 건의 마지막 값은 화면에 노출하지 않는다 */
  barCode?: string;
  /** 수정 이력 조회 키 */
  barCodeStaticIdx?: number;
  /** 저장 동작 판정에 쓰는 원본. null 이면 한 번도 등록한 적 없음 */
  barCodeInfo?: IEmployeeCardPaymentBarCodeInfo | null;
}

export const normalizeRow = (item: any): IEmployeeCardPaymentRow => {
  const userInfo = item.userInfo || {};
  const barCodeInfo = item.barCodeInfo || null;
  const status = userInfo.status || {};
  // 삭제된(isActive:false) 건은 마지막 barCode 가 내려오지만 미등록으로 다룬다.
  const isRegistered = !!(barCodeInfo && barCodeInfo.isActive);

  return {
    userId: userInfo.userId,
    signId: userInfo.signId,
    name: userInfo.name,
    groupIdx: userInfo.groupIdx,
    groupName: userInfo.groupName,
    orgCode: userInfo.orgCode,
    orgPathName: userInfo.orgPathName,
    email: userInfo.email,
    phone: userInfo.cellphone,
    employeeId: userInfo.employeeId,
    statusCode: status.code,
    statusText: status.text,
    joinDate: userInfo.joinDate,
    barCode: isRegistered ? barCodeInfo.barCode : undefined,
    barCodeStaticIdx: barCodeInfo ? barCodeInfo.barCodeStaticIdx : undefined,
    barCodeInfo
  };
};

export type EmployeeCardPaymentSaveAction = 'NONE' | 'CREATE' | 'UPDATE' | 'DELETE';

/**
 * [저장] 시 수행할 동작을 barCodeInfo 상태 × 입력값으로 정한다.
 * - 미등록(null) / 삭제됨(isActive:false) : 값 채움 → 등록 / 빈칸 → 아무것도 안 함
 *   (삭제됨은 기존 barCodeStaticIdx 행을 재사용해 되살린다)
 * - 등록됨(isActive:true) : 같은 값 → 아무것도 안 함 / 다른 값 → 수정 / 빈칸 → 삭제
 * 같은 값인지는 양쪽을 정규화한 값으로 본다(대소문자·공백만 다르면 같은 값).
 */
export const resolveSaveAction = (
  barCodeInfo: IEmployeeCardPaymentBarCodeInfo | null | undefined,
  inputValue: string
): EmployeeCardPaymentSaveAction => {
  const value = normalizeEmployeeCardPaymentCode(inputValue);

  if (!barCodeInfo || !barCodeInfo.isActive) {
    return value ? 'CREATE' : 'NONE';
  }
  if (!value) return 'DELETE';
  return value === normalizeEmployeeCardPaymentCode(barCodeInfo.barCode) ? 'NONE' : 'UPDATE';
};

export const getEmployeeCardPaymentList = async (params: IEmployeeCardPaymentSearchParams) => {
  const { data } = await COMPANY_API.get(LIST_ENDPOINT, { params });
  const content = (data && data.content) || [];

  return { rows: content.map(normalizeRow), totalCount: (data && data.totalCount) || 0 };
};

/** 사원증 결제정보 사용자 상세 — 응답은 목록 content[] 원소와 같은 모양이다. */
export const getEmployeeCardPaymentDetail = async (userId: string) => {
  const { data } = await COMPANY_API.get(`${LIST_ENDPOINT}/${userId}`);
  return normalizeRow(data || {});
};

/*
  저장 실패는 상세 모달의 토스트와 엑셀 미리보기의 '비고'로 화면에서 직접 알린다.
  공통 인터셉터의 안내 모달까지 뜨면 중복이고, 엑셀 일괄 저장에서는 실패 건마다 모달이 뜨므로 끈다.
*/
const SKIP_ERROR_MODAL = { skipErrorModal: true } as any;

/** ⚠️ TODO 요청 body 형태 확인 필요(지금은 { barCode }) */
export const updateEmployeeCardPayment = async (userId: string, barCode: string) => {
  await COMPANY_API.put(`${LIST_ENDPOINT}/${userId}`, { barCode }, SKIP_ERROR_MODAL);
};

/**
 * 지울 사원증 결제정보가 없을 때 서버가 내려주는 코드.
 * 서버는 실패로 보지만 사용자 입장에선 '비어 있는 상태'라는 결과가 같으므로 성공으로 다룬다.
 */
const NOTHING_TO_DELETE_STATUS = -1012;

const isNothingToDeleteError = (error: any) => {
  const data = error && error.response && error.response.data;
  return !!data && Number(data.status) === NOTHING_TO_DELETE_STATUS;
};

/** soft delete */
export const deleteEmployeeCardPayment = async (userId: string) => {
  try {
    await COMPANY_API.delete(`${LIST_ENDPOINT}/${userId}`, SKIP_ERROR_MODAL);
  } catch (error) {
    if (!isNothingToDeleteError(error)) throw error;
  }
};

/**
 * 입력값이 비어 있으면 삭제, 값이 있으면 등록/수정. 보내는 값은 정규화한 값이다.
 * 상세 모달과 엑셀 일괄 저장이 같은 규칙을 쓴다.
 */
export const saveEmployeeCardPayment = (userId: string, barCode: string) => {
  const value = normalizeEmployeeCardPaymentCode(barCode);
  return value ? updateEmployeeCardPayment(userId, value) : deleteEmployeeCardPayment(userId);
};

/*
  비동기 엑셀 생성 흐름
    1) 생성 요청(POST .../excel) → fileId
    2) 잠시 뒤 상태 확인(GET /addons/v1/excel/{fileId}/status)
    3) status 가 DOWNLOAD(5) 면 파일명을 찾아 내려받기(GET /addons/v1/excel/{fileId})
  다른 회사 파일이면 -11, 없는 fileId 면 조회 오류가 내려온다.
*/
const EXCEL_FILE_ENDPOINT = '/addons/v1/excel';
const EXCEL_STATUS_DOWNLOAD = 5;
// 엑셀 다운로드 내역 화면과 같은 페이지 크기
const EXCEL_LIST_PAGE_ROW = 15;

/**
 * 사원증 결제정보 엑셀 생성 요청. 내려받기에 필요한 fileId 와 파일명을 돌려준다.
 * 201 응답에 '저장되었습니다' 모달을 띄우는 successHandler 를 타지 않도록 NO_RESULT 인스턴스를 쓴다.
 */
export const requestEmployeeCardPaymentExcel = async () => {
  const { data } = await COMPANY_API_NO_RESULT_RESPONSE.post(`${LIST_ENDPOINT}/excel`);

  // ⚠️ TODO 응답의 fileId 필드명 확인 필요
  return {
    fileId: (data && (data.fileId || data.id)) || null,
    fileName: (data && data.name) || null
  };
};

/** 생성 상태 조회. 응답이 숫자인지 객체인지 확정 전이라 양쪽을 받는다. */
export const getExcelFileStatus = async (fileId: string) => {
  const { data } = await COMPANY_API.get(`${EXCEL_FILE_ENDPOINT}/${fileId}/status`);
  return data;
};

/**
 * 내려받기 쿼리에 넣을 파일명 조회.
 * 생성 요청·상태 조회 응답에 이름이 없어 엑셀 다운로드 내역 목록에서 같은 fileId 행을 찾는다.
 * 내역 화면도 이 목록의 file.name 을 그대로 쿼리에 실어 보낸다(components/ExcelDownloadList/ExcelList.jsx).
 * 방금 만든 파일이라 첫 페이지에 있다.
 */
export const getExcelFileName = async (fileId: string) => {
  const { data } = await COMPANY_API.get(EXCEL_FILE_ENDPOINT, { params: { page: 1, pagerow: EXCEL_LIST_PAGE_ROW } });
  const rows = (data && data.excel) || [];
  const matched = rows.filter((row: any) => row.file && row.file.id === fileId)[0];

  return (matched && matched.file.name) || null;
};

export const isExcelReady = (status: any) => {
  const code = status && typeof status === 'object' ? status.status : status;
  return Number(code) === EXCEL_STATUS_DOWNLOAD;
};

/**
 * 생성된 엑셀 내려받기. 파일명이 content-disposition 에 오므로 응답 전체를 돌려준다.
 *
 * 이 엔드포인트는 헤더와 별개로 filetype/comid/requestid/name 쿼리를 모두 요구한다 — 하나라도 빠지면 500 이 난다.
 * 엑셀 다운로드 내역 화면도 같은 값을 붙인다(services/commons.js 의 ExcelDownload).
 */
export const downloadExcelFile = (fileId: string, fileName: string) => {
  const company: any = storage.get('company') || {};

  return COMPANY_API_NO_RESULT_RESPONSE.get(`${EXCEL_FILE_ENDPOINT}/${fileId}`, {
    params: {
      filetype: 'excel',
      comid: company.id,
      requestid: company.user && company.user.id,
      name: fileName
    },
    responseType: 'arraybuffer'
  });
};

export interface IEmployeeCardPaymentHistory {
  /** 수정된 사원증 결제정보 */
  barCode?: string;
  /** 구분 — 등록 / 수정 / 삭제. text 가 표시용 라벨이다 */
  action?: { code?: string; text?: string };
  userId?: string;
  /** 수정자 */
  userName?: string;
  /** 수정일시 */
  regDate?: string;
}

/** 사원증 결제정보 수정 기록 (페이징) */
export const getEmployeeCardPaymentHistory = async (
  barCodeStaticIdx: number,
  params: { page?: number; size?: number } = {}
) => {
  const { data } = await COMPANY_API.get(`${HISTORY_ENDPOINT}/${barCodeStaticIdx}`, { params });

  return {
    content: ((data && data.content) || []) as IEmployeeCardPaymentHistory[],
    totalCount: (data && data.totalCount) || 0
  };
};
